import { useState, useEffect, useRef, useCallback } from "react";

const DEFAULT_DELAY = 300;
const MAX_DELAY = 30000;

/**
 * General-purpose debounce hook for value debouncing.
 * 
 * @param {*} value - The value to debounce
 * @param {number} delay - Debounce delay in milliseconds (default: 300, max: 30000)
 * @returns {*} The debounced value
 */
const useDebounce = (value, delay = DEFAULT_DELAY) => {
  const safeDelay = Math.min(Math.max(0, Number(delay) || DEFAULT_DELAY), MAX_DELAY);
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, safeDelay);

    return () => clearTimeout(timer);
  }, [value, safeDelay]);

  return debouncedValue;
};

/**
 * Hook for debouncing callback functions with pending state tracking.
 * 
 * @param {Function} callback - The callback function to debounce
 * @param {number} delay - Debounce delay in milliseconds (default: 300, max: 30000)
 * @returns {Object} Object containing:
 *   - debouncedCallback: The debounced function
 *   - isPending: Boolean indicating if a debounce is pending
 *   - cancel: Function to cancel the pending debounce
 *   - flush: Function to execute immediately and clear the timer
 *   - reset: Function to restart the debounce timer
 */
const useDebouncedCallback = (callback, delay = DEFAULT_DELAY) => {
  const safeDelay = Math.min(Math.max(0, Number(delay) || DEFAULT_DELAY), MAX_DELAY);
  const [isPending, setIsPending] = useState(false);
  
  const callbackRef = useRef(callback);
  const timerRef = useRef(null);
  const argsRef = useRef(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsPending(false);
    argsRef.current = null;
  }, []);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    
    if (argsRef.current !== null && callbackRef.current) {
      try {
        callbackRef.current(...argsRef.current);
      } catch (error) {
        console.error('Error executing debounced callback:', error);
      }
    }
    
    setIsPending(false);
    argsRef.current = null;
  }, []);

  const reset = useCallback(() => {
    if (timerRef.current && argsRef.current !== null) {
      clearTimeout(timerRef.current);
      
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setIsPending(false);
        
        if (callbackRef.current && argsRef.current !== null) {
          try {
            callbackRef.current(...argsRef.current);
          } catch (error) {
            console.error('Error executing debounced callback:', error);
          }
        }
        argsRef.current = null;
      }, safeDelay);
    }
  }, [safeDelay]);

  const debouncedCallback = useCallback((...args) => {
    if (typeof callbackRef.current !== 'function') {
      console.warn('useDebouncedCallback: callback is not a function');
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    argsRef.current = args;
    setIsPending(true);

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setIsPending(false);
      
      if (callbackRef.current) {
        try {
          callbackRef.current(...args);
        } catch (error) {
          console.error('Error executing debounced callback:', error);
        }
      }
      argsRef.current = null;
    }, safeDelay);
  }, [safeDelay]);

  return {
    debouncedCallback,
    isPending,
    cancel,
    flush,
    reset,
  };
};

export { useDebounce, useDebouncedCallback };
export default useDebounce;
